import React, { useState, useEffect } from 'react'
import ClientDropdown from './ClientDropdown'
import { ConvergedClientView } from './converged/ConvergedClientView'
import { transformPortfolioData } from './converged/dataTransform'
import type { GovernedClient } from '../../../types'
import { useAuthStore } from '../../../store/authStore'
import { useClientPortfolio } from '../../../hooks/useClientPortfolio'
import { useGovernedClients } from '../../../hooks/useGovernedClients'
import './ConvergedDataSection.css'

const ConvergedDataSection: React.FC = () => {
  const { isReadOnlyMode } = useAuthStore()
  const [selectedClient, setSelectedClient] = useState<GovernedClient | null>(null)
  const [isManualLoading, setIsManualLoading] = useState(false)
  const { portfolioData, loading: portfolioLoading, error: portfolioError, fetchClientPortfolio, clearData } = useClientPortfolio()
  const { clients, loading: clientsLoading } = useGovernedClients()

  // Auto-select first client once clients are loaded
  useEffect(() => {
    if (!clientsLoading && clients.length > 0 && !selectedClient) {
      handleClientSelect(clients[0])
    }
  }, [clientsLoading, clients])

  const handleClientSelect = async (client: GovernedClient) => {
    clearData()
    setSelectedClient(client)
    setIsManualLoading(true)

    if (client.client_identifier) {
      try {
        await fetchClientPortfolio(client.client_identifier)
      } catch (error) {
        console.error('Error fetching client portfolio:', error)
      } finally {
        setIsManualLoading(false)
      }
    } else {
      setIsManualLoading(false)
    }
  }


  return (
    <div className="converged-data-section">
      {/* Read-Only Mode Banner */}
      {isReadOnlyMode && (
        <div className="readonly-banner">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#856404"
            strokeWidth="2"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span className="readonly-text">
            Read-Only Mode: You can view data but cannot make changes.
          </span>
        </div>
      )}

      {/* Client Selection */}
      <div className="client-selection-container">
        <div className="selection-label">
          <label htmlFor="client-dropdown">Select Client</label>
          <p className="selection-description">
            Search for a client by name or ID to view their data
          </p>
        </div>

        <div className="dropdown-container">
          <ClientDropdown
            selectedClientId={selectedClient?.id || null}
            onClientSelect={handleClientSelect}
            placeholder="Search and select a client..."
            disabled={isReadOnlyMode}
            clients={clients}
            loading={clientsLoading}
          />
        </div>
      </div>

      {/* Client Portfolio Details */}
      {(clientsLoading || selectedClient) ? (
        <div className="client-details">
          {(clientsLoading || portfolioLoading || isManualLoading) ? (
            <div className="portfolio-loading-state">
              <div className="loading-spinner"></div>
              <span className="loading-text">Loading portfolio data...</span>
            </div>
          ) : portfolioError ? (
            <div className="portfolio-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              <span>Failed to load portfolio data: {portfolioError.message}</span>
            </div>
          ) : portfolioData ? (
            <ConvergedClientView {...transformPortfolioData(portfolioData)} />
          ) : (
            <div className="empty-state">
              <p>No portfolio data available for this client.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="no-client-selected">
          <div className="no-client-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3 className="no-client-title">No Client Selected</h3>
          <p className="no-client-description">
            Please select a client from the dropdown above to view their governed data and insights.
          </p>
        </div>
      )}
    </div>
  )
}

export default ConvergedDataSection